import { canReadDomain } from '@/api/core/authorization';
import {
  readUnitPricingEdit,
  setUnitPricing,
  type UnitPricingEdit,
  type UnitPricingInput,
  type UnitPricingValues,
} from '@/api/core/unit-pricing';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import { Button, ConfirmDialog, Input } from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createHeldStore, type HeldUnitRequest } from './held-unit-requests';
import type { PropertyRuntime } from './property-queries';

/**
 * 슬롯(Unit) 개별 가격. 로컬 BE candidate(010, 미배포) 계약을 따른다.
 * 편집 조회가 준 cas를 그대로 expected로 되돌려 보내고(stale은 서버가 거절), 빈칸은 상속(NULL),
 * 0은 보증금·청소비의 명시 면제다. 결과는 같은 명령의 응답과 다시 읽은 현재 가격으로만 확정한다.
 */
interface Props {
  runtime: PropertyRuntime;
  generation: number;
  unit: { id: string; name: string };
  roomName: string;
  onChanged?: () => void;
  /** 이 슬롯의 보존 요청이 생기거나 사라졌을 때 알린다(다른 슬롯 화면의 안내 갱신용). */
  onHeldChange?: () => void;
  /** 같은 슬롯의 판매 전환 요청 결과가 아직 확인되지 않았음(이 브라우저가 보존 중인 요청). */
  transitionPending?: boolean;
  /** 아직 저장하지 않은 입력이 있는지 알린다(화면을 떠날 때 경고하는 데 쓴다). */
  onDirtyChange?: (dirty: boolean) => void;
}
interface Intent extends HeldUnitRequest {
  actorId: string;
  input: UnitPricingInput;
}
// 메모리에만 둔다: 화면을 다시 그려도 결과 불명 요청을 잃지 않는다(슬롯 단위).
const retained = createHeldStore<Intent>();
export const pendingUnitPriceRequests = (runtime: PropertyRuntime, generation: number) =>
  retained.pending(
    runtime.client,
    runtime.apiNamespace,
    generation,
    access({ runtime, generation }),
  );
const access = ({ runtime, generation }: Pick<Props, 'runtime' | 'generation'>) => {
  const a = runtime.session.getSnapshot();
  return (
    a.generation === generation &&
    a.status === 'authorized' &&
    a.authorization !== null &&
    !a.deniedDomains.includes('booking') &&
    canReadDomain(a.authorization, 'booking', 1)
  );
};
const labels: Record<keyof UnitPricingValues, string> = {
  regular_amount_minor: '일 정가',
  deposit_amount: '보증금',
  cleaning_fee: '청소비',
};
const sourceLabel = (kind: string | null | undefined) =>
  kind === 'UNIT'
    ? '슬롯 설정'
    : kind === 'ROOM'
      ? '객실 설정 상속'
      : kind === 'TYPE'
        ? '객실 유형 상속'
        : '출처 미확인';
const text = (value: number | null) => (value === null ? '' : String(value));
const parse = (raw: string, minimum: number): number | null | 'invalid' => {
  const value = raw.trim();
  if (value === '') return null;
  if (!/^\d+$/.test(value)) return 'invalid';
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= minimum ? n : 'invalid';
};
const sameValues = (a: UnitPricingValues, b: UnitPricingValues) =>
  (Object.keys(labels) as (keyof UnitPricingValues)[]).every((key) => a[key] === b[key]);
const current = (edit: UnitPricingEdit): UnitPricingValues => ({
  regular_amount_minor: edit.unit_policy?.regular_amount_minor ?? null,
  deposit_amount: edit.unit_policy?.deposit_amount ?? null,
  cleaning_fee: edit.unit_policy?.cleaning_fee ?? null,
});

export function UnitPricingSection(props: Props) {
  const auth = useSyncExternalStore(
    props.runtime.session.subscribe,
    props.runtime.session.getSnapshot,
    props.runtime.session.getSnapshot,
  );
  const key = JSON.stringify([props.runtime.apiNamespace, props.generation, props.unit.id, auth]);
  return <Pricing key={key} {...props} />;
}

function Pricing(props: Props) {
  const { runtime, generation } = props;
  const [edit, setEdit] = useState<UnitPricingEdit | null>(null);
  const [held, setHeld] = useState<Intent | null>(
    () => retained.get(runtime.client, props.unit.id) ?? null,
  );
  const [reviewed, setReviewed] = useState<Intent | null>(null);
  const [inputs, setInputs] = useState<Record<keyof UnitPricingValues, string>>({
    regular_amount_minor: '',
    deposit_amount: '',
    cleaning_fee: '',
  });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const alive = useRef(true);
  const lock = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    props.onHeldChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 보존 요청이 바뀔 때만 알린다.
  }, [held]);
  const dirty =
    !!edit &&
    !held &&
    (reason !== '' ||
      (Object.keys(labels) as (keyof UnitPricingValues)[]).some(
        (key) => inputs[key] !== text(current(edit)[key]),
      ));
  useEffect(() => {
    props.onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 입력이 바뀔 때만 알린다.
  }, [dirty]);
  useEffect(
    () => () => {
      props.onDirtyChange?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면이 사라질 때만 해제한다.
    [],
  );
  const valid = () => alive.current && access(props);
  const actor = async () => {
    const a = await runtime.client.get<{ user_id?: unknown }>('users/me', {
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!valid() || !isUuid(a.user_id)) throw new Error('Actor unavailable');
    return a.user_id;
  };
  const fail = (e: unknown, message: string) => {
    if (!alive.current) return;
    setFeedback(
      isApiError(e) && e.status === 404
        ? '슬롯 가격 정보를 찾을 수 없습니다. 이 서버가 슬롯 단위 가격을 지원하는지 확인하세요.'
        : message,
    );
    if (isApiError(e) && e.status === 403)
      runtime.session.reportDomainAccessDenied('booking', generation);
  };
  const load = (fresh: UnitPricingEdit) => {
    setEdit(fresh);
    const values = current(fresh);
    setInputs({
      regular_amount_minor: text(values.regular_amount_minor),
      deposit_amount: text(values.deposit_amount),
      cleaning_fee: text(values.cleaning_fee),
    });
  };
  const read = async () => {
    if (!valid() || lock.current) return;
    lock.current = true;
    setBusy(true);
    setEdit(null);
    setReviewed(null);
    setFeedback('');
    try {
      const fresh = await readUnitPricingEdit(runtime.client, props.unit.id);
      if (valid()) load(fresh);
    } catch (e) {
      fail(e, '슬롯 가격을 확인하지 못했습니다.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const parsed = {
    regular_amount_minor: parse(inputs.regular_amount_minor, 1),
    deposit_amount: parse(inputs.deposit_amount, 0),
    cleaning_fee: parse(inputs.cleaning_fee, 0),
  };
  const inputsValid = Object.values(parsed).every((v) => v !== 'invalid');
  const values = inputsValid ? (parsed as UnitPricingValues) : null;
  const unchanged = !!edit && !!values && sameValues(values, current(edit));
  const blockedReason = (() => {
    if (!edit) return '슬롯 가격을 먼저 조회하세요.';
    if (!inputsValid) return '금액은 0 이상의 정수(정가는 1 이상)로 입력하세요. 비우면 상속입니다.';
    if (unchanged) return '현재 설정과 다른 값을 입력하세요.';
    if (!reason.trim()) return '변경 사유를 입력하세요.';
    return '변경 내용을 검토할 수 있습니다.';
  })();
  const review = async () => {
    if (!valid() || lock.current || held || !edit || !values) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      const actorId = await actor();
      const fresh = await readUnitPricingEdit(runtime.client, props.unit.id);
      if (!valid() || (await actor()) !== actorId) throw new Error('Actor changed');
      if (JSON.stringify(fresh.cas) !== JSON.stringify(edit.cas)) {
        load(fresh);
        throw new Error('Pricing changed');
      }
      setReviewed({
        namespace: runtime.apiNamespace,
        generation,
        unitId: props.unit.id,
        unitName: props.unit.name,
        roomName: props.roomName,
        actorId,
        input: {
          command_id: crypto.randomUUID(),
          expected: fresh.cas,
          values,
          reason,
        },
      });
    } catch (e) {
      fail(e, '슬롯 가격 설정이 바뀌었습니다. 현재 값을 다시 확인한 뒤 검토하세요.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const owns = (i: Intent) =>
    valid() &&
    i.namespace === runtime.apiNamespace &&
    i.generation === generation &&
    i.unitId === props.unit.id;
  const save = async (i: Intent, replay: boolean) => {
    if (!owns(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    let sent = false;
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      if (!replay) {
        retained.set(runtime.client, i);
        setHeld(i);
        setReviewed(null);
      }
      sent = true;
      await setUnitPricing(runtime.client, i.unitId, i.input);
      const fresh = await readUnitPricingEdit(runtime.client, i.unitId);
      if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
      if (!sameValues(current(fresh), i.input.values)) throw new Error('Readback mismatch');
      retained.delete(runtime.client, i.unitId);
      setHeld(null);
      load(fresh);
      setReason('');
      setFeedback('슬롯 가격 변경을 확인했습니다. 다시 조회한 값과 일치합니다.');
      props.onChanged?.();
    } catch (e) {
      const definitive =
        sent &&
        !replay &&
        isApiError(e) &&
        e.kind === 'http' &&
        e.status !== undefined &&
        e.status >= 400 &&
        e.status < 500;
      if (definitive && retained.get(runtime.client, i.unitId) === i) {
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
      }
      fail(
        e,
        definitive
          ? isApiError(e) && e.status === 409
            ? edit?.unit_policy === null
              ? '서버가 변경을 거절했습니다. 이 슬롯에는 아직 개별 가격 정책이 없어 슬롯의 최초 가격은 판매 전환에서 정할 수 있습니다. 현재 값을 다시 조회하세요.'
              : '서버가 변경을 거절했습니다. 가격 설정이 먼저 바뀌었거나 현재 상태에서 받아들이지 않는 변경일 수 있습니다. 현재 값을 다시 조회하세요.'
            : '서버가 요청을 거절했습니다. 입력과 권한을 확인하세요.'
          : '변경 결과가 불명확합니다. 처음 요청을 보존했으며 자동 재전송하지 않습니다.',
      );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  // 현재 가격 확인은 읽기뿐이라, 같은 namespace·같은 수행자(users/me로 확인)이면 로그인 generation이 바뀐 뒤에도 허용한다.
  const ownsRead = (i: Intent) =>
    valid() && i.namespace === runtime.apiNamespace && i.unitId === props.unit.id;
  const inspect = async (i: Intent) => {
    if (!ownsRead(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      const fresh = await readUnitPricingEdit(runtime.client, i.unitId);
      if ((await actor()) !== i.actorId || !ownsRead(i)) throw new Error('Actor changed');
      load(fresh);
      if (sameValues(current(fresh), i.input.values)) {
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
        setReason('');
        setFeedback(
          '현재 가격이 원래 요청의 값과 같은 것을 확인했습니다. 다른 변경이 같은 값을 만든 경우는 구분할 수 없습니다.',
        );
        props.onChanged?.();
      } else {
        setFeedback(
          '현재 가격에서 원래 요청의 반영을 확인하지 못했습니다. 처음 요청을 보존했습니다.',
        );
      }
    } catch (e) {
      fail(e, '현재 가격을 확인하지 못했습니다. 처음 요청을 보존했습니다.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const allowed = access(props);
  const ownHeld = held?.namespace === runtime.apiNamespace && held.unitId === props.unit.id;
  const sameSession = ownHeld && held.generation === generation;
  const blocked = busy || !!held || !!reviewed;
  return (
    <section
      aria-label="슬롯 개별 가격"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <h4 className="font-semibold">
        {props.roomName} · {props.unit.name} 슬롯 가격
      </h4>
      <p>
        빈칸은 객실·객실 유형의 값을 상속합니다. 보증금·청소비 0은 명시 면제입니다. 이 슬롯의 가격만
        바뀌며 같은 객실의 다른 슬롯은 바뀌지 않습니다.
      </p>
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={!allowed || busy}
        onClick={() => void read()}
      >
        슬롯 가격 조회
      </Button>
      {!allowed && <p>슬롯 가격 조회에 Booking 조회 권한이 필요합니다.</p>}
      {props.transitionPending && (
        <p role="status">
          이 슬롯의 판매 전환 요청 결과가 아직 확인되지 않았습니다. 전환 결과를 확인하기 전에는 가격
          변경이 서버에서 거절될 수 있습니다.
        </p>
      )}
      {edit && (
        <>
          <div role="status" className="space-y-1">
            {(Object.keys(labels) as (keyof UnitPricingValues)[]).map((key) => {
              const field = edit.effective_fields[key];
              return (
                <p key={key}>
                  현재 {labels[key]}:{' '}
                  {field.value === null
                    ? `확인 불가${field.reason ? ` (${field.reason})` : ''}`
                    : field.value === 0 && key !== 'regular_amount_minor'
                      ? '0원(명시 면제)'
                      : `${field.value.toLocaleString('ko-KR')}원`}{' '}
                  · {sourceLabel(field.source_kind)}
                </p>
              );
            })}
            {edit.unit_policy === null && <p>이 슬롯에는 개별 가격 정책이 아직 없습니다.</p>}
          </div>
          {!held && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {(Object.keys(labels) as (keyof UnitPricingValues)[]).map((key) => (
                  <label key={key} className="grid gap-1">
                    {labels[key]} (원, 빈칸=상속)
                    <Input
                      aria-label={`슬롯 ${labels[key]}`}
                      inputMode="numeric"
                      disabled={blocked}
                      value={inputs[key]}
                      aria-invalid={parsed[key] === 'invalid'}
                      onChange={(e) => {
                        setInputs({ ...inputs, [key]: e.target.value });
                      }}
                    />
                  </label>
                ))}
              </div>
              <label className="grid gap-1">
                가격 변경 사유
                <Input
                  aria-label="슬롯 가격 변경 사유"
                  value={reason}
                  disabled={blocked}
                  maxLength={500}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </label>
              <p id="unit-pricing-block" role="status">
                {blockedReason}
              </p>
              <Button
                aria-describedby="unit-pricing-block"
                className="h-auto min-h-8 whitespace-normal"
                disabled={
                  !allowed ||
                  blocked ||
                  !inputsValid ||
                  unchanged ||
                  !reason.trim() ||
                  Array.from(reason).length > 500
                }
                onClick={() => void review()}
              >
                슬롯 가격 변경 검토
              </Button>
            </>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!reviewed}
        onOpenChange={(open) => {
          if (!open) setReviewed(null);
        }}
        target={`${props.roomName} · ${props.unit.name} 슬롯`}
        action="슬롯 가격 변경"
        consequence={`${(Object.keys(labels) as (keyof UnitPricingValues)[])
          .map((key) => {
            const v = reviewed?.input.values[key];
            return `${labels[key]} ${v === null || v === undefined ? '상속' : `${v.toLocaleString('ko-KR')}원`}`;
          })
          .join(
            ' · ',
          )} · 사유 ${reviewed?.input.reason ?? ''}. 확정된 기존 예약 금액은 바뀌지 않습니다.`}
        variant="default"
        onConfirm={() => {
          if (reviewed) void save(reviewed, false);
        }}
      />
      {held && (
        <>
          <p role="alert">
            원래 가격 변경 요청을 보존했습니다. 새 요청은 차단하고 자동 재전송하지 않습니다.
            {sameSession && allowed && <> 대상: {held.unitName} 슬롯.</>}
          </p>
          {ownHeld && !sameSession && (
            <p role="status">
              이전 로그인에서 접수한 요청입니다. 같은 수행자로 확인되면 현재 가격을 조회할 수 있고,
              같은 요청을 다시 보내는 것은 현재 로그인에서는 막혀 있습니다.
            </p>
          )}
          {ownHeld && (
            <div className="flex flex-wrap items-center gap-2">
              {sameSession && (
                <Button
                  className="h-auto min-h-8 whitespace-normal"
                  disabled={!allowed || busy}
                  onClick={() => void save(held, true)}
                >
                  같은 요청 다시 시도
                </Button>
              )}
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={!allowed || busy}
                onClick={() => void inspect(held)}
              >
                현재 가격으로 확인
              </Button>
            </div>
          )}
        </>
      )}
      {feedback && <p role="status">{feedback}</p>}
    </section>
  );
}
